/**
 * Owner of the PZ/WZ list split into "Aktywne" and "Anulowane i storno"
 * (owner decision 2026-09-18): cancelled and reversed documents must not
 * clutter the operator's default view, but stay reachable in one place.
 *
 * The view lives in the URL (`?view=cancelled`) like other list filters, so
 * links and reloads land on the same category. Both list endpoints return the
 * whole set (no server pagination), so the split is a pure client concern.
 */

export type WarehouseDocumentListView = 'active' | 'cancelled';

export const WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM = 'view';

export const WAREHOUSE_DOCUMENT_LIST_VIEWS: readonly WarehouseDocumentListView[] = ['active', 'cancelled'];

/** Any unknown/missing value falls back to the default operator view. */
export function parseWarehouseDocumentListView(raw: string | null | undefined): WarehouseDocumentListView {
  return raw === 'cancelled' ? 'cancelled' : 'active';
}

/**
 * How a cancelled document ended up cancelled.
 * - `CANCELLED`: plain cancellation (draft/confirmed never affected stock).
 * - `STORNO`: reversal of a received document (stock already moved, then
 *   reversed by a correction of kind STORNO — separate "Storno przyjętego PZ" task).
 */
export type CancelledDocumentKind = 'CANCELLED' | 'STORNO';

/**
 * Minimal shape the helper reads. PZ storno (migration 170) stamps
 * `stornoAt` on the document: `status === CANCELLED` + `stornoAt` is a storno,
 * `CANCELLED` without it is a plain cancellation. `cancellationKind` is an
 * optional explicit discriminator kept for WZ, whose storno is not built yet;
 * feeding either field is all a future storno flow needs — not the pages.
 */
export interface CancellableWarehouseDocument {
  status?: string | null;
  stornoAt?: string | null;
  cancellationKind?: string | null;
}

export function isCancelledWarehouseDocument(doc: CancellableWarehouseDocument): boolean {
  return doc.status === 'CANCELLED';
}

export function getCancelledDocumentKind(doc: CancellableWarehouseDocument): CancelledDocumentKind | null {
  if (!isCancelledWarehouseDocument(doc)) return null;
  if (doc.stornoAt || doc.cancellationKind === 'STORNO') return 'STORNO';
  return 'CANCELLED';
}

export function partitionWarehouseDocumentsByView<T extends CancellableWarehouseDocument>(
  docs: readonly T[],
): { active: T[]; cancelled: T[] } {
  const active: T[] = [];
  const cancelled: T[] = [];
  for (const doc of docs) {
    (isCancelledWarehouseDocument(doc) ? cancelled : active).push(doc);
  }
  return { active, cancelled };
}

export function selectWarehouseDocumentsForView<T extends CancellableWarehouseDocument>(
  docs: readonly T[],
  view: WarehouseDocumentListView,
): T[] {
  const { active, cancelled } = partitionWarehouseDocumentsByView(docs);
  return view === 'cancelled' ? cancelled : active;
}
